import React, { FC, useEffect, useState } from 'react'
import {
  Container,
  Heading,
  Box,
  Flex,
  IconButton,
} from '@moonpig/launchpad-components'
import { Carousel } from '@moonpig/web-shared-components'
import { styled } from '@moonpig/launchpad-utils'
import { spacingPx } from '@moonpig/launchpad-theme'
import { system as s } from '@moonpig/launchpad-system'
import { GuidedNBATile } from './GuidedNBATile'
import type { GuidedNBATile as GuidedNBATileType } from './types'
import { useInView } from 'react-intersection-observer'
import {
  trackGAEvent,
  useTrackGAEventOnce,
  createGenericSelectContentEvent,
} from '@moonpig/web-core-analytics'
import { createViewItemListGAEvent, createSelectItemEvent } from './tracking'
import { useStoreId } from '@moonpig/web-core-stores'
import { IconSystemCross } from '@moonpig/launchpad-assets'
import { useFindLocaleText } from '../../text-localisation'

const StyledContent = styled(Box)`
  position: relative;
  ${s({
    pt: { xs: 2, md: 4 },
    px: { xs: 0, md: 6, xl: 0 },
    pb: { xs: 4, xl: 7 },
  })}
`

const CLOSE_BUTTON_SIZE = spacingPx(8)

const StyledCloseButton = styled(IconButton)`
  ${s({
    mr: { md: -6 },
  })}

  svg {
    width: ${CLOSE_BUTTON_SIZE};
    height: ${CLOSE_BUTTON_SIZE};
  }
`

export type GuidedNBACarouselProps = {
  title: string
  items: GuidedNBATileType[]
  isCards: boolean
}

export const GuidedNBACarousel: FC<
  React.PropsWithChildren<GuidedNBACarouselProps>
> = ({ title, items, isCards }) => {
  const localiseText = useFindLocaleText()
  const [closed, setClosed] = useState(false)
  const { ref, inView } = useInView({
    triggerOnce: true,
  })
  const { trackGAEventOnce } = useTrackGAEventOnce()
  const storeId = useStoreId()

  useEffect(() => {
    if (inView) {
      trackGAEventOnce(createViewItemListGAEvent({ items, title, storeId }))
    }
  }, [inView, trackGAEventOnce, items, title, storeId])

  const handleOnClick = (tile: GuidedNBATileType, index: number) => {
    trackGAEvent(
      createSelectItemEvent({
        tile,
        index,
        title,
        items,
        storeId,
      }),
    )
  }

  const handleClose = () => {
    trackGAEvent(
      createGenericSelectContentEvent({
        eventCategory: 'gallery action',
        eventAction: 'close guided nbas',
        eventLabel: title,
      }),
    )
    setClosed(true)
  }

  return items.length > 0 && !closed ? (
    <Box bgcolor="colorBackground03">
      <Container limitWidth padding={0}>
        <StyledContent data-testid="guided-nba-carousel" ref={ref}>
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="end"
          >
            <Heading level="h5" mb={4} pl={{ xs: 6, md: 0 }}>
              {title}
            </Heading>
            <StyledCloseButton
              icon={IconSystemCross}
              label={localiseText('common.close')}
              id="close-guided-nba-heading"
              onClick={handleClose}
            />
          </Flex>
          <Carousel
            ariaLabel={title}
            itemsGap={{ xs: 8 }}
            skipLinkHref="#close-guided-nba-heading"
            $noSpacing
            alignItems="center"
          >
            {items.map((tile, index) => (
              <GuidedNBATile
                key={tile.title}
                {...tile}
                isCardImage={isCards}
                onClick={() => handleOnClick(tile, index)}
              />
            ))}
          </Carousel>
        </StyledContent>
      </Container>
    </Box>
  ) : null
}
